import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Linkedin, Mail, ArrowLeft, Phone, Globe, Instagram } from 'lucide-react';
import Button from '../../components/Button/Button';
import './TeamMember.css';

interface TeamMemberData {
  id: string;
  name: string;
  role: string;
  image: string;
  shortBio: string;
  fullBio: string[];
  personalSnapshot?: string;
  quote?: string;
  quoteAuthor?: string;
  phone?: string;
  linkedin?: string;
  email?: string;
  website?: string;
  instagram?: string;
}

const teamMembersData: Record<string, TeamMemberData> = {
  'ritesh-nautiyal': {
    id: 'ritesh-nautiyal',
    name: 'Ritesh Nautiyal',
    role: 'Founder and CEO',
    image: '/ritesh.png',
    shortBio: 'Over two decades of experience across four continents in tennis and racquet sports, specializing in program development, strategic leadership, and high-performance management.',
    fullBio: [
      'Ritesh Nautiyal is the Founder and CEO of Baseline Racquets Consultants of America, a premier global consultancy specializing in tennis and racquet sports. With over two decades of experience across four continents, Ritesh brings a unique blend of playing, coaching, and business expertise to the company. His work focuses on providing tailored solutions in program development, strategic leadership, and high-performance management, helping tennis clubs, coaches, and organizations achieve sustainable growth through data-driven strategies and innovative leadership.',
      'Ritesh has held several key roles in the tennis industry, including Director of Tennis at The Philadelphia Cricket Club and Director of Racquet Sports, alongside serving as a touring coach for ATP and WTA professionals. His broad experience enables him to address the diverse challenges facing racquet sports organizations, ranging from performance enhancement to operational efficiency. Through his consulting, he has been instrumental in modernizing the operations, programming, and player pathways of clubs and academies, moving them toward scalable, dynamic growth models.',
      'At Baseline, Ritesh and his team focus on integrating athletic performance with business strategy, always prioritizing long-term, sustainable success for their members. His leadership philosophy—founded on empathy, structure, and collaboration—drives the company\'s approach to member relationships, ensuring impactful, lasting results. Recognized for his technical expertise and commitment to player development, Ritesh holds certifications from leading global coaching bodies, including RSPA, PTR, USPR, and UFDORS.',
      'In addition to his consulting work, Ritesh continues to serve as Director of Tennis at The Philadelphia Cricket Club, where he oversees high-performance programs and further demonstrates his leadership in advancing the racquet sports industry.',
    ],
    personalSnapshot: 'When he is not teaching or playing racquet sports, Ritesh enjoys traveling with family and friends, experiencing new cultures, and meeting new people.',
    quote: 'Excellence is not a skill — it\'s an attitude that separates the best from the rest.',
    quoteAuthor: 'Ralph Marston',
    phone: '941-228-6054',
    linkedin: 'https://www.linkedin.com/in/ritesh-nautiyal-240a31147',
    email: 'nautiyalritesh6@gmail.com',
  },
  'shashank-nautiyal': {
    id: 'shashank-nautiyal',
    name: 'Shashank Nautiyal',
    role: 'Partner',
    image: '/shanky.png',
    shortBio: 'Seasoned racquets-industry leader with 15+ years of experience across private clubs and commercial operations. Former Director of Racquets at Genesis Health Clubs.',
    fullBio: [
      'Shashank Nautiyal is a Partner of Baseline, and a seasoned racquets-industry leader with more than 15 years of experience across private clubs and commercial racquets operations. As former Director of Racquets at Genesis Health Clubs, one of the top racquet companies in the United States, he led multi-sport programs and increased revenues and profits by triple digits.',
      'Currently serving as Head Racquets Professional at The Elkridge Club in Baltimore, Maryland, Shashank is recognized for his hospitality-driven leadership, innovative programming, and commitment to professional education. A past RSPA Board Member and two-time USPTA Missouri Valley Professional of the Year, he holds advanced degrees in Sports Management, Exercise Physiology, and Business Administration, along with certifications from: CMAA, DCA, University of Florida Director of Racquet Sports, CRSE (in progress), USPTA, PTR, PPR, PPTR, and ITPA.',
      'Through Baseline, Shashank empowers racquet professionals and clubs to grow with structure, purpose, and leadership excellence, helping them educate, evolve, and excel in every area of the industry.',
    ],
    personalSnapshot: 'Off the court, Shashank Nautiyal lives an active and balanced lifestyle focused on fitness, proper nutrition, and continuous learning. He believes that energy, mindset, and education are the foundations of great leadership both in life and in racquets. Whether he is training, mentoring professionals, studying new ideas, or spending time with his wife, Shashank brings the same passion, positivity, and focus that define his work on and off the court.',
    quote: 'Educate. Evolve. Excel. The way we lead defines the legacy we leave.',
    quoteAuthor: 'Unknown',
    phone: '765-409-6826',
    linkedin: 'https://www.linkedin.com/in/shashank-nautiyal-7087aa211',
    email: 'shashankhill@gmail.com',
  },
  'robert-driscoll': {
    id: 'robert-driscoll',
    name: 'Robert Driscoll',
    role: 'Chief Operating Officer',
    image: '/rob.png',
    shortBio: 'Former nationally ranked squash player and finance executive managing over $600M in assets.',
    fullBio: [
      'As Chief Operating Officer of Baseline, Rob Driscoll combines analytical precision, operational leadership, and a lifelong passion for racquet sports to help drive the firm\'s global mission. With a background in finance and a deep understanding of high-performance environments, Rob ensures that every Baseline program, partnership, and member experience is executed with excellence and integrity.',
      'Before joining Baseline, Rob managed over $600 million in assets at SEI Investments, overseeing seven hedge funds within the Alternative Investments division. His experience in fund accounting, financial modeling, and operational reporting honed his ability to transform complex data into clear, actionable strategy—skills that now underpin Baseline\'s data-driven approach to performance and growth.',
      'Rob\'s early experience in private equity and commercial real estate—with firms such as Intrinsic Capital Partners and Meridian Bank—further strengthened his ability to identify opportunities, optimize performance, and guide organizations through strategic expansion.',
      'A graduate of the University of Richmond, Rob earned his Bachelor of Business Administration in Finance, graduating cum laude. As co-captain of the Men\'s Squash Team, he competed nationally and developed a leadership style rooted in teamwork, discipline, and continuous improvement—values that now define his approach at Baseline.',
      'At Baseline, Rob oversees operations, strategic partnerships, and organizational growth, ensuring every member, coach, and player benefits from a seamless, world-class experience. Passionate about all things racquet sports, he is driven by Baseline\'s belief that every great journey begins with a strong foundation—and that true success comes from combining structure, strategy, and purpose.',
    ],
    personalSnapshot: 'Outside of work, Rob is an avid tennis and padel player, and enjoys staying active through hiking and fitness. He also loves traveling, cooking, and spending quality time with friends and family. As a fun fact, Rob used to be a nationally ranked squash player.',
    quote: 'Whatever the mind of man can conceive and believe, it can achieve.',
    quoteAuthor: 'Napoleon Hill',
    phone: '484-343-8586',
    linkedin: 'https://www.linkedin.com/in/rob-driscoll-81b208206',
    email: 'rob.driscoll9@gmail.com',
  },
  'freddie-mitchell': {
    id: 'freddie-mitchell',
    name: 'Freddie Mitchell',
    role: 'Head of Talent Acquisition',
    image: '/freddie.png',
    shortBio: 'Former NFL first-round draft pick and Philadelphia Eagles legend. Author of the iconic "4th and 26" play. Now dedicated to sports psychology, mental health advocacy, and mentoring athletes.',
    fullBio: [
      'Freddie Mitchell rose out of Lakeland, Florida and stamped his name into football history. At UCLA, he authored the single greatest receiving season the program has ever seen — a record that still stands today. With uncanny hands, game-breaking speed, and an unshakable will, he did so much with so little opportunity, earning the respect of teammates and fans across the country. Drafted in the first round by the legendary Andy Reid, Mitchell stepped straight into the heart of the NFL\'s Golden Age in Philadelphia. He became a centerpiece in one of the most dominant eras the Eagles have ever known — four consecutive NFC Championship games and a Super Bowl appearance. Philly fans will never forget the moment that defined him: 4th and 26, the play that immortalized "FredEx" as the man who always delivered.',
      'Known as "The True #84," Mitchell brought charisma, confidence, and authenticity to the league — sometimes to his own cost, as his outspoken nature got him pushed aside before the game became watered down with fines, softened rules, and spoiled stars. But that only cemented his status as a legend: a man who played with passion, thanked his hands on national TV, and left behind memories that still echo in the stands of Lincoln Financial Field.',
      'Today, Mitchell\'s impact reaches beyond the field. He is deeply involved in sports psychology and mental health advocacy, drawing on his own journey to uplift athletes navigating the pressures of the game. He mentors young Eagles, college players, and youth athletes, offering guidance, wisdom, and the hard truths about the work it takes to succeed — and the mindset it takes to endure. Freddie Mitchell isn\'t just a former player. He\'s a storyteller, a motivator, and a living reminder of an era when grit, heart, and belief could change everything. This is FredEx. This is 4th and 26. This is The Man. The Legend. Freddie Mitchell.',
    ],
    personalSnapshot: 'When he is not working on community projects or mentoring young athletes, Freddie enjoys racquet sports, fishing in the ocean, and golfing. He loves traveling the world to experience different cultures, and finds true fulfillment in helping people and giving back to the community.',
    quote: 'Successful people do what unsuccessful people are not willing to do. Don\'t wish it were easier; wish you were better.',
    quoteAuthor: 'Jim Rohn',
    phone: '813-842-1289',
    linkedin: 'http://linkedin.com/in/freddie-mitchell-b4131a125',
    email: 'fmitchell8406@gmail.com',
    instagram: '@fmitchell84',
  },
  'tomas-kostik': {
    id: 'tomas-kostik',
    name: 'Tomas Kostik',
    role: 'Partner',
    image: '/default.png',
    shortBio: 'Former top-10 nationally ranked junior in Germany with over two decades as a high-performance player, coach, and consultant, integrating analytics with elite development.',
    fullBio: [
      'Tomas is a Partner at Baseline, bringing over two decades of experience as a high-performance player, coach, and consultant. A former top-10 nationally ranked junior in Germany, Tomas began competing at a young age before moving to the world-renowned IMG Academy (formerly the Nick Bollettieri Tennis Academy) in 1996. There, he trained and traveled alongside many of the world\'s top professional players throughout his junior career, gaining a deep understanding of elite-level development and competition.',
      'After completing his training in 2002, Tomas pursued his academic interests, earning a degree in Mathematics from Temple University. His analytical background led him to a stint in finance, where he traded on the New York Mercantile Exchange—an experience that refined his decision-making, risk assessment, and data-driven thinking, all of which now inform his approach to player and program development.',
      'Returning to his first passion, Tomas transitioned into tennis coaching and consulting, working with highly ranked national players through the USTA and serving at premier institutions including the USTA Training Center at Legacy, Philadelphia Cricket Club, White Manor Country Club, Radnor Hunt Country Club, and Unlimited Tennis Academy. His work integrates high-performance training with strategic program design, helping players and organizations optimize both athletic and operational excellence.',
      'At Baseline, Tomas leverages his unique blend of tennis knowledge, competitive experience, and industry insight to provide comprehensive solutions in performance development, data analytics, and organizational strategy. His philosophy centers on measurable progress, individualized training, and building sustainable systems for long-term success.',
    ],
    personalSnapshot: 'When he\'s not on court or working with players, Tomas enjoys exploring new technologies, studying market trends, and spending time with family.',
    quote: 'High performance isn\'t a burst of excellence—it\'s the outcome of systems that make excellence repeatable.',
    quoteAuthor: 'Unknown',
    phone: '917-294-1984',
    linkedin: 'https://www.linkedin.com/',
    email: 'tk24700@gmail.com',
  },
  'saif-ali': {
    id: 'saif-ali',
    name: 'Saif Ali',
    role: 'Partner',
    image: '/saif.png',
    shortBio: 'Global tennis consultant with 20+ years of international experience across four continents. Former touring coach for ATP professionals including former world No. 1 Rohan Bopanna.',
    fullBio: [
      'Saif Ali is a Partner at Baseline, a global tennis and sports consulting firm helping clubs, coaches, and organizations achieve sustainable, high-performance growth. With over 20 years of international experience across four continents, Saif blends elite coaching expertise with strategic business insight to drive transformation in the racquet-sports industry.',
      'Before joining Baseline, Saif held leadership roles including Director of Tennis at the Anantara Resort & Spa in Dubai, Director of Racquet Sports at Sparta Athletic Campus, and touring coach for ATP professionals, including former world No. 1 Rohan Bopanna. His leadership philosophy—rooted in empathy, structure, and collaboration—guides Baseline\'s mission to bridge athletic performance, business strategy, and organizational culture.',
      'A certified coach and performance specialist through the USPTA, PTR, GPTCA/ATP, ITPA, RPT, PPR, and WTCA, Saif also contributes to USTA education programs and leads Baseline\'s Coaching Development Curriculum, mentoring the next generation of coaches and leaders. He believes that true success in sport comes from aligning culture, clarity, and consistency. At Baseline, he champions the principle that "while we work in the business of tennis, we\'re really in the business of relationships." His leadership style fosters trust, accountability, and shared ownership—values that define Baseline\'s internal culture and member partnerships alike.',
    ],
    personalSnapshot: 'Outside of Baseline, Saif remains an active player, traveler, and learner. He enjoys exploring global tennis cultures, participating in leadership networks, and mentoring professionals in both sports and business. His personal ethos mirrors his professional one—continuous growth through purpose, reflection, and connection.',
    quote: 'Excellence isn\'t accidental, it\'s the result of alignment between vision and values, strategy and execution, and people and purpose.',
    quoteAuthor: 'Unknown',
    phone: '917-873-0472',
    linkedin: 'https://www.linkedin.com/in/saif-ali-485b75198',
    email: 'saifalitennis@gmail.com',
    website: 'athlostennis.com',
  },
};

const TeamMember: React.FC = () => {
  const { memberId } = useParams<{ memberId: string }>();
  
  if (!memberId || !teamMembersData[memberId]) {
    return <Navigate to="/about" replace />;
  }

  const member = teamMembersData[memberId];

  return (
    <div className="team-member-page">
      {/* Hero Section */}
      <section className="team-member-hero">
        <div className="container">
          <div>
            <Button variant="outline" size="small" href="/about" className="back-button">
              <ArrowLeft size={16} style={{ marginRight: '8px' }} />
              Back to Team
            </Button>
          </div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="section team-member-profile">
        <div className="container">
          <div className="profile-grid">
            <div className="profile-image-wrapper">
              <img
                src={member.image}
                alt={member.name}
                className="profile-image"
              />
              {member.quote && (
                <div className="profile-quote">
                  <p className="quote-text">"{member.quote}"</p>
                  {member.quoteAuthor && (
                    <p className="quote-author">— {member.quoteAuthor}</p>
                  )}
                </div>
              )}
              <div className="profile-social">
                {member.linkedin && (
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${member.name} LinkedIn`}
                    className="social-link"
                  >
                    <Linkedin size={24} />
                  </a>
                )}
                {member.email && (
                  <a
                    href={`mailto:${member.email}`}
                    aria-label={`Email ${member.name}`}
                    className="social-link"
                  >
                    <Mail size={24} />
                  </a>
                )}
              </div>
            </div>

            <div className="profile-content">
              <h1 className="profile-name">{member.name}</h1>
              <p className="profile-role">{member.role}</p>
              
              {/* Contact Info */}
              {(member.phone || member.email || member.linkedin || member.website || member.instagram) && (
                <div className="profile-contact-info">
                  {member.phone && (
                    <div className="contact-item">
                      <Phone size={18} />
                      <a href={`tel:${member.phone}`}>{member.phone}</a>
                    </div>
                  )}
                  {member.email && (
                    <div className="contact-item">
                      <Mail size={18} />
                      <a href={`mailto:${member.email}`}>{member.email}</a>
                    </div>
                  )}
                  {member.linkedin && (
                    <div className="contact-item">
                      <Linkedin size={18} />
                      <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                        {member.linkedin.replace('https://', '').replace('http://', '')}
                      </a>
                    </div>
                  )}
                  {member.website && (
                    <div className="contact-item">
                      <Globe size={18} />
                      <a href={`https://${member.website}`} target="_blank" rel="noopener noreferrer">
                        {member.website}
                      </a>
                    </div>
                  )}
                  {member.instagram && (
                    <div className="contact-item">
                      <Instagram size={18} />
                      <a href={`https://instagram.com/${member.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer">
                        {member.instagram}
                      </a>
                    </div>
                  )}
                </div>
              )}
              
              <div className="profile-bio">
                {member.fullBio.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              {/* Personal Snapshot */}
              {member.personalSnapshot && (
                <div className="personal-snapshot">
                  <h3>Personal Snapshot</h3>
                  <p>{member.personalSnapshot}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section team-member-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Work With {member.name.split(' ')[0]}</h2>
            <p>
              Interested in learning more about how {member.name.split(' ')[0]} can help transform your racquet sports business?
            </p>
            <div className="cta-buttons">
              <Button variant="primary" size="large" href="/contact">
                Get in Touch
              </Button>
              <Button variant="outline" size="large" href="https://calendar.app.google/zeaz1ocNpttMocbv7">
                Schedule a Call
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeamMember;

